import { normalizePassengerTypes } from 'utils/passengerCategories';
import { ORDERED_PASSENGER_CATEGORIES } from 'constants/passengerCategories';

/**
 * Allocate selected seats to passenger categories positionally.
 *
 * Iterates ORDERED_PASSENGER_CATEGORIES consuming seats per selected type.
 * Trailing seats (more seats than the selection size) accumulate under
 * `general` (adulto). When the selection is empty/single-type, all seats
 * fall under `general`. Pure function — same input always yields same output,
 * so the pricing breakdown and the zone sub-rows stay in sync.
 *
 * @param {object} options
 * @param {Array} [options.regularSeats=[]] - Selected seats already filtered of adjacents.
 * @param {object} [options.passengerSelection={}] - e.g. { general: 1, older: 1, minor: 1 } (aliases tolerated).
 * @returns {Object<string, Array>} Mapping canonical category → seats[]. Empty when no seats.
 */
export const allocateSeatsByCategory = ({ regularSeats = [], passengerSelection = {} } = {}) => {
  const allocation = {};
  if (!regularSeats.length) return allocation;

  const normalized = normalizePassengerTypes(passengerSelection || {});
  const hasMultiType = Object.values(normalized).some((count) => count > 0);

  if (!hasMultiType) {
    allocation.general = [...regularSeats];
    return allocation;
  }

  let seatCursor = 0;
  ORDERED_PASSENGER_CATEGORIES.forEach((type) => {
    const typeCount = normalized[type] || 0;
    if (typeCount <= 0) return;
    const seatsForType = regularSeats.slice(seatCursor, seatCursor + typeCount);
    seatCursor += typeCount;
    if (seatsForType.length > 0) allocation[type] = seatsForType;
  });

  const trailing = regularSeats.slice(seatCursor);
  if (trailing.length > 0) {
    allocation.general = [...(allocation.general || []), ...trailing];
  }

  return allocation;
};

export default allocateSeatsByCategory;
